import React, { useLayoutEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/apiClient';
import { getAuthToken, saveAuthSession } from '../utils/authStorage';
import { saveProfile, getStoredProfile } from '../utils/profileStorage';
import './AuthLayout.css';

const Login = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [selectedRole, setSelectedRole] = useState('user');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useLayoutEffect(() => {
    if (getAuthToken()) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (event) => {
    setSelectedRole(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/auth/login', form);

      const user = response.data?.user;

      // If a role is selected and it does not match the user's role, deny access.
      // Assumes backend sends something like `user.role` (e.g. 'admin', 'manager', 'developer', 'viewer').
      if (user?.role && user.role.toLowerCase() !== selectedRole.toLowerCase()) {
        setError('Access denied: you do not have permission to log in as this role.');
        setLoading(false);
        return;
      }

      saveAuthSession({
        token: response.data?.token,
        user: response.data?.user,
      });
      
      // Sync profile picture to profile storage on login
      const currentProfile = getStoredProfile();
      saveProfile({ ...currentProfile, avatar: response.data?.user?.profilePicture || '' });
      
      navigate('/dashboard');
    } catch (err) {
      const message =
        err.response?.data?.message || err.message || 'Unable to log in. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        <h1>Sign In</h1>
        <p className="auth-subtitle">Access your WebMonitor dashboard</p>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit} className="auth-form">
          <label htmlFor="login-role">Role</label>
          <select
            id="login-role"
            name="role"
            value={selectedRole}
            onChange={handleRoleChange}
            required
          >
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="technician">Technician</option>
            <option value="user">User</option>
          </select>

          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            name="email"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={handleChange}
            required
            placeholder="you@example.com"
          />

          <label htmlFor="login-password">Password</label>
          <div className="password-field">
            <input
              id="login-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={form.password}
              onChange={handleChange}
              required
              placeholder="Enter password"
            />
          </div>

          <button type="submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <p className="auth-footer">
          New here? <Link to="/register">Create an account</Link>
        </p>
      </div>
    </div>
  );
};

export default Login;


